"use client";

import { useEffect, useState } from "react";
import { Apple, Check, Copy, Download, KeyRound, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Descarga del Chismoso + (si viene del correo) el enlace directo que lo abre
 * ya con la invitación. El instalador es el MISMO para todos; lo personal es
 * el enlace `qualidot://conectar?token=…`, que el sistema le pasa al Chismoso.
 *
 * Los pasos van como línea de tiempo: descargar → abrir → (dentro del
 * Chismoso) crear la contraseña. El tercero no tiene botón a propósito: dice
 * qué va a pasar para que nadie lo busque aquí.
 */
export function DescargaChismoso({ macUrl, version, megas, enlace, codigo }: {
  macUrl: string | null; version: string; megas?: number;
  /** Solo con invitación: `qualidot://conectar?token=…&server=…`. */
  enlace?: string | null;
  /** Solo con invitación: el link completo, para pegarlo si el enlace no abre. */
  codigo?: string | null;
}) {
  const [esWindows, setEsWindows] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [ayuda, setAyuda] = useState(false);
  useEffect(() => { setEsWindows(/Win/i.test(navigator.userAgent)); }, []);

  const copiar = async () => {
    if (!codigo) return;
    try { await navigator.clipboard.writeText(codigo); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sin permiso */ }
  };

  return (
    <div className="space-y-6">
      <ol className="relative space-y-6">
        {/* El hilo que une los pasos */}
        <span aria-hidden className="absolute left-[13px] top-7 bottom-7 w-px bg-border" />

        <Paso n={1} titulo="Descarga el Chismoso"
          detalle="La app que graba tus llamadas y sesiones y las manda a evaluar.">
          {macUrl ? (
            <a href={macUrl} download className="block">
              <Button className="h-10 w-full gap-2 text-[13px]">
                <Apple className="h-4 w-4" /> Descargar para macOS
                <span className="font-normal opacity-75 tabular-nums">· v{version}{megas ? ` · ${megas} MB` : ""}</span>
              </Button>
            </a>
          ) : (
            <Button className="h-10 w-full gap-2" disabled><Apple className="h-4 w-4" /> macOS · disponible muy pronto</Button>
          )}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11.5px] text-muted-foreground">
            <span className={esWindows ? "text-warning" : ""}>Windows y Linux: próximamente</span>
            <button type="button" onClick={() => setAyuda((v) => !v)} aria-expanded={ayuda}
              className="font-medium text-primary hover:underline">¿macOS no lo deja abrir?</button>
          </div>
          {ayuda && (
            <div className="space-y-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-[11.5px] text-muted-foreground">
              <p>macOS avisa «Apple no puede revisar si contiene software malicioso» con las apps que no vienen de la App Store. Es seguro abrirlo; solo hay que confirmarlo <b className="text-foreground">una vez</b>:</p>
              <ol className="list-decimal space-y-1 pl-5">
                <li>Arrastra <b className="text-foreground">Qualidot Chismoso</b> a Aplicaciones y ábrelo (sale el aviso: dale OK).</li>
                <li>Ve a <b className="text-foreground">Configuración del Sistema → Privacidad y seguridad</b>.</li>
                <li>Abajo, junto a «Se bloqueó Qualidot Chismoso», dale <b className="text-foreground">Abrir igualmente</b> y confirma.</li>
              </ol>
              <p>En macOS 14 o anterior también funciona <b className="text-foreground">clic derecho sobre el ícono → Abrir</b>.</p>
            </div>
          )}
        </Paso>

        {enlace ? (
          <>
            <Paso n={2} titulo="Ábrelo con tu invitación"
              detalle="Ya instalado, este botón lo abre con tu correo puesto.">
              <a href={enlace} className="block">
                <Button variant="outline" className="h-10 w-full gap-2 text-[13px]"><MousePointerClick className="h-4 w-4" /> Abrir el Chismoso</Button>
              </a>
              {codigo && (
                <button type="button" onClick={copiar}
                  className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-primary hover:underline">
                  {copiado ? <><Check className="h-3 w-3" /> Link copiado</> : <><Copy className="h-3 w-3" /> ¿No se abrió? Copia tu link y pégalo en el Chismoso</>}
                </button>
              )}
            </Paso>
            <Paso n={3} titulo="Crea tu contraseña" icono={<KeyRound className="h-3.5 w-3.5" />}
              detalle="Dentro del Chismoso: escríbela, confírmala y listo. Con ella entras también al portal a ver tus evaluaciones." />
          </>
        ) : (
          <Paso n={2} titulo="Entra con tu cuenta"
            detalle="Ábrelo y entra con el correo y la contraseña de Qualidot. ¿Te invitaron por correo? Usa el botón de ese correo: el Chismoso abre con tu cuenta lista." />
        )}
      </ol>

      <div className="flex flex-col items-center gap-2 border-t border-border pt-4">
        <Cuadros />
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Download className="h-3 w-3" /> Un solo instalador para todos: tu cuenta la pone el enlace.
        </p>
      </div>
    </div>
  );
}

function Paso({ n, titulo, detalle, icono, children }: {
  n: number; titulo: string; detalle: string; icono?: React.ReactNode; children?: React.ReactNode;
}) {
  return (
    <li className="relative flex gap-3.5">
      <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-[12px] font-semibold text-primary-foreground tabular-nums ring-4 ring-card">
        {icono ?? n}
      </span>
      <div className="min-w-0 flex-1 space-y-2.5 pt-0.5">
        <div>
          <h2 className="text-[13.5px] font-semibold text-foreground">{titulo}</h2>
          <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{detalle}</p>
        </div>
        {children}
      </div>
    </li>
  );
}

/** Los 10 cuadros de desempeño de Qualidot, de rojo a verde: la firma de la marca. */
function Cuadros() {
  const colores = ["#e5484d", "#e5484d", "#e5484d", "#f76b15", "#f76b15", "#f5b40a", "#f5b40a", "#30a46c", "#30a46c", "#30a46c"];
  return (
    <span aria-hidden className="flex gap-[3px]">
      {colores.map((c, i) => <i key={i} className="block h-2 w-2 rounded-[2px]" style={{ background: c, opacity: 0.35 + i * 0.065 }} />)}
    </span>
  );
}
